import { parsePoB, fetchPoBFromUrl, uploadToPobbIn, UnsupportedPoBError, type ParsedItem } from './pob-parser';
import { loadStatIndex } from './stat-matcher';
import { buildLinks, rebuildUrl, type BuildResult, type EditableFilter } from './trade-url';

const $ = <T extends HTMLElement>(sel: string): T => {
  const el = document.querySelector<T>(sel);
  if (!el) throw new Error(`Element nicht gefunden: ${sel}`);
  return el;
};

const input = $<HTMLTextAreaElement>('#pob-input');
const leagueSelect = $<HTMLSelectElement>('#league');
const itemsetSelect = $<HTMLSelectElement>('#itemset');
const viewModeSelect = $<HTMLSelectElement>('#viewmode');
const sectionSelect = $<HTMLSelectElement>('#section');
const minPctSelect = $<HTMLSelectElement>('#minpct');
const saveSettingsCb = $<HTMLInputElement>('#save-settings');

const SETTINGS_KEY = 'poblinks.settings.v1';
function loadSavedSettings(): void {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return;
    const s = JSON.parse(raw);
    saveSettingsCb.checked = true;
    if (s.league && Array.from(leagueSelect.options).some(o => o.value === s.league)) {
      leagueSelect.value = s.league;
    }
    if (s.view) viewModeSelect.value = s.view;
    if (s.section) sectionSelect.value = s.section;
    if (s.minPct) minPctSelect.value = s.minPct;
  } catch {/* ignore */}
}
function saveSettings(): void {
  if (!saveSettingsCb.checked) {
    try { localStorage.removeItem(SETTINGS_KEY); } catch {}
    return;
  }
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify({
      league: leagueSelect.value,
      view: viewModeSelect.value,
      section: sectionSelect.value,
      minPct: minPctSelect.value,
    }));
  } catch {/* ignore quota */}
}
loadSavedSettings();

// Populate the league dropdown from the backend (mirrors PoE trade leagues),
// so it never goes stale after a league rollover. Hardcoded <option>s in
// index.html act as the fallback if the fetch fails.
async function populateLeagues(): Promise<void> {
  try {
    const r = await fetch('/api/leagues', { cache: 'no-cache' });
    if (!r.ok) return;
    const j = await r.json();
    const leagues: Array<{ id: string; text: string }> = Array.isArray(j.leagues) ? j.leagues : [];
    if (!leagues.length) return;
    const prev = leagueSelect.value; // saved/default selection from loadSavedSettings
    leagueSelect.replaceChildren();
    for (const l of leagues) {
      const opt = document.createElement('option');
      opt.value = l.id;
      opt.textContent = l.text;
      leagueSelect.appendChild(opt);
    }
    // Keep the prior choice if it still exists, else fall back to the first
    // entry (the current temp league, which the API lists first).
    leagueSelect.value = leagues.some(l => l.id === prev) ? prev : leagues[0].id;
  } catch { /* keep hardcoded fallback options */ }
}
populateLeagues();

// Warm the 365 KB stat index in the background once the browser is idle, so the
// first "Generate links" click feels instant instead of waiting on the download.
// loadStatIndex() is a no-op if already loaded, so the later await is free.
const prefetchStatIndex = () => { void loadStatIndex().catch(() => {}); };
if ('requestIdleCallback' in window) {
  (window as Window & { requestIdleCallback: (cb: () => void) => void })
    .requestIdleCallback(prefetchStatIndex);
} else {
  setTimeout(prefetchStatIndex, 1500);
}

saveSettingsCb.addEventListener('change', saveSettings);
const generateBtn = $<HTMLButtonElement>('#generate');
const status = $<HTMLSpanElement>('#status');
const resultsSection = $<HTMLElement>('#results');
const buildInfoEl = $<HTMLDivElement>('#build-info');
const equipmentEl = $<HTMLDivElement>('#equipment');
const jewelsEl = $<HTMLDivElement>('#jewels');
const jewelsHeading = $<HTMLHeadingElement>('#jewels-heading');
const gemsEl = $<HTMLDivElement>('#gems');
const gemsHeading = $<HTMLHeadingElement>('#gems-heading');

let lastPobCode: string | null = null;

// ═════════════ Stats counter ═════════════
const statPobsEl = document.getElementById('stat-pobs');
const statClicksEl = document.getElementById('stat-clicks');
function fmtNum(n: number): string { return n.toLocaleString('en-US'); }
async function refreshStats(): Promise<void> {
  try {
    const r = await fetch('/api/stats', { cache: 'no-cache' });
    if (!r.ok) return;
    const s = await r.json();
    if (statPobsEl) statPobsEl.textContent = fmtNum(s.pobs ?? 0);
    if (statClicksEl) statClicksEl.textContent = fmtNum(s.clicks ?? 0);
    if (Array.isArray(s.recent)) renderRecent(s.recent);
  } catch {/* offline ok */}
}
interface RecentEntry {
  ts: number; cls?: string; asc?: string; lvl?: number; pobbId?: string; skill?: string;
  life?: number; lifePercent?: number; es?: number; ward?: number; mana?: number; ehp?: number;
  fireRes?: number; coldRes?: number; lightningRes?: number; chaosRes?: number;
  evadeChance?: number; blockChance?: number; pdr?: number;
  totalDPS?: number; speed?: number; hitChance?: number; critChance?: number; critMulti?: number;
  bandit?: string; pantheonMajor?: string; pantheonMinor?: string; configSummary?: string;
}

function trackPob(meta?: Record<string, unknown>, recentOnly = false): void {
  const url = recentOnly ? '/api/track/recent' : '/api/track/pob';
  try {
    if (meta) {
      const blob = new Blob([JSON.stringify(meta)], { type: 'application/json' });
      if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
      else fetch(url, { method: 'POST', body: blob, keepalive: true }).catch(() => {});
    } else {
      if (navigator.sendBeacon) navigator.sendBeacon(url);
      else fetch(url, { method: 'POST', keepalive: true }).catch(() => {});
    }
  } catch {}
}

const recentEl = document.getElementById('recent-pobs');
const recentListEl = document.getElementById('recent-list');
function relativeTime(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
let recentVisible = 10;
let recentCache: RecentEntry[] = [];

function fmtBig(n: number | undefined): string {
  if (n == null) return '—';
  if (n >= 1e9) return (n / 1e9).toFixed(2) + 'b';
  if (n >= 1e6) return (n / 1e6).toFixed(2) + 'm';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'k';
  return Math.round(n).toString();
}

function buildRecentTooltip(r: RecentEntry): string {
  const lines: string[] = [];
  const cls = esc(r.cls || '?');
  const asc = r.asc && r.asc !== r.cls ? ' / ' + esc(r.asc) : '';
  lines.push(`<div class="tt-title">${cls}${asc} — Level ${r.lvl ?? '?'}</div>`);
  const def: string[] = [];
  if (r.life != null) def.push(`Life ${fmtBig(r.life)}${r.lifePercent != null ? ` (${r.lifePercent}%)` : ''}`);
  if (r.es) def.push(`ES ${fmtBig(r.es)}`);
  if (r.ward) def.push(`Ward ${fmtBig(r.ward)}`);
  if (r.mana != null) def.push(`Mana ${fmtBig(r.mana)}`);
  if (r.ehp != null) def.push(`eHP ${fmtBig(r.ehp)}`);
  if (def.length) lines.push(`<div class="tt-line">${def.join(' · ')}</div>`);
  const res: string[] = [];
  if (r.fireRes != null || r.coldRes != null || r.lightningRes != null || r.chaosRes != null) {
    res.push(`Res ${r.fireRes ?? '?'}/${r.coldRes ?? '?'}/${r.lightningRes ?? '?'}/${r.chaosRes ?? '?'}`);
  }
  if (r.evadeChance != null) res.push(`Evade ${Math.round(r.evadeChance)}%`);
  if (r.blockChance) res.push(`Block ${Math.round(r.blockChance)}%`);
  if (r.pdr) res.push(`PDR ${Math.round(r.pdr)}%`);
  if (res.length) lines.push(`<div class="tt-line">${res.join(' · ')}</div>`);
  const dps: string[] = [];
  if (r.totalDPS != null) dps.push(`<b>DPS ${fmtBig(r.totalDPS)}</b>`);
  if (r.speed != null) dps.push(`Speed ${r.speed.toFixed(2)}`);
  if (r.hitChance != null) dps.push(`Hit ${Math.round(r.hitChance)}%`);
  if (r.critChance != null) dps.push(`Crit ${r.critChance.toFixed(1)}%`);
  if (r.critMulti != null) dps.push(`Multi ${Math.round(r.critMulti)}%`);
  if (dps.length) lines.push(`<div class="tt-line tt-dps">${dps.join(' · ')}</div>`);
  const meta: string[] = [];
  if (r.bandit && r.bandit !== 'None') meta.push(`Bandit: ${esc(r.bandit)}`);
  if (r.pantheonMajor) meta.push(`Pantheon: ${esc(r.pantheonMajor.replace(/^TheBrineKing$/, 'Brine King').replace(/([a-z])([A-Z])/g, '$1 $2'))}${r.pantheonMinor ? ', ' + esc(r.pantheonMinor.replace(/([a-z])([A-Z])/g, '$1 $2')) : ''}`);
  if (r.configSummary) meta.push(`Config: ${esc(r.configSummary)}`);
  if (meta.length) lines.push(`<div class="tt-line tt-meta">${meta.join('<br>')}</div>`);
  return lines.join('');
}

function renderRecent(recent: RecentEntry[]): void {
  if (!recentEl || !recentListEl) return;
  recentCache = recent || [];
  if (!recentCache.length) { recentEl.classList.add('hidden'); return; }
  recentEl.classList.remove('hidden');
  recentListEl.innerHTML = '';
  const slice = recentCache.slice(0, recentVisible);
  for (const r of slice) {
    const li = document.createElement('li');
    li.className = 'recent-item';

    const build = document.createElement('div');
    build.className = 'recent-build';
    const cls = r.cls || '?';
    const asc = r.asc && r.asc !== r.cls ? ' / ' + r.asc : '';
    const lvl = r.lvl ? ' — Level ' + r.lvl : '';
    build.textContent = `${cls}${asc}${lvl}`;
    li.appendChild(build);

    const meta = document.createElement('div');
    meta.className = 'recent-meta';
    meta.appendChild(document.createTextNode(relativeTime(r.ts)));
    if (r.pobbId) {
      meta.appendChild(document.createTextNode(' · '));
      const a = document.createElement('a');
      a.href = `https://pobb.in/${r.pobbId}`;
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = 'pobb.in';
      meta.appendChild(a);
    }
    li.appendChild(meta);

    // Hover tooltip with full stats
    const tt = document.createElement('div');
    tt.className = 'recent-tooltip';
    tt.innerHTML = buildRecentTooltip(r);
    li.appendChild(tt);

    recentListEl.appendChild(li);
  }

  // Load-more / show-less control
  const ctrl = document.createElement('div');
  ctrl.className = 'recent-control';
  if (recentVisible < recentCache.length) {
    const more = document.createElement('button');
    more.textContent = `Load more (${recentCache.length - recentVisible} more)`;
    more.addEventListener('click', () => { recentVisible = Math.min(recentVisible + 10, recentCache.length); renderRecent(recentCache); });
    ctrl.appendChild(more);
  }
  if (recentVisible > 10) {
    const less = document.createElement('button');
    less.textContent = 'Show less';
    less.addEventListener('click', () => { recentVisible = 10; renderRecent(recentCache); });
    ctrl.appendChild(less);
  }
  if (ctrl.children.length) recentListEl.appendChild(ctrl);
}
function trackClick(): void {
  try {
    if (navigator.sendBeacon) navigator.sendBeacon('/api/track/click');
    else fetch('/api/track/click', { method: 'POST', keepalive: true }).catch(() => {});
  } catch {}
}
refreshStats();
setInterval(refreshStats, 30000); // every 30s

// Delegate click tracking on all trade links
document.addEventListener('click', (ev) => {
  const target = ev.target as HTMLElement | null;
  const link = target?.closest('a.trade-link');
  if (link) {
    trackClick();
    setTimeout(refreshStats, 800);
  }
});

const PSEUDO_LABEL: Record<string, string> = {
  life: 'Total Life', es: 'Total ES', mana: 'Total Mana',
  str: 'Total Str', dex: 'Total Dex', int: 'Total Int',
  fire_res: 'Total Fire Res', cold_res: 'Total Cold Res',
  lightning_res: 'Total Lightning Res', chaos_res: 'Total Chaos Res',
  elemental_res: 'Total Ele Res', life_regen: 'Life Regen/sec',
  all_attr: 'All Attr (triple)',
};

function modClass(raw: string): string {
  if (raw.includes('{crafted}')) return 'crafted';
  if (raw.includes('{fractured}')) return 'fractured';
  if (raw.includes('{enchant}')) return 'enchant';
  return '';
}

function cleanMod(raw: string): string {
  return raw.replace(/\{[^}]*\}/g, '').trim();
}

function setStatus(msg: string, cls: '' | 'success' | 'error' = ''): void {
  status.textContent = msg;
  status.className = 'status' + (cls ? ' ' + cls : '');
}

function rarityClass(r: ParsedItem['rarity']): string {
  switch (r) {
    case 'UNIQUE': return 'unique';
    case 'RARE': return 'rare';
    case 'MAGIC': return 'magic';
    case 'RELIC': return 'relic';
    default: return 'normal';
  }
}

function renderItemTable(
  slotLabel: string,
  item: ParsedItem,
  result: BuildResult,
  rowIndex: number,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'tbl-row';

  const slot = document.createElement('div');
  slot.className = 'tbl-slot';
  slot.textContent = slotLabel;
  row.appendChild(slot);

  const nameWrap = document.createElement('div');
  nameWrap.className = 'tbl-name';
  const nameEl = document.createElement('span');
  nameEl.className = 'name-text ' + rarityClass(item.rarity);
  nameEl.textContent = item.name || '(Unknown)';
  nameWrap.appendChild(nameEl);
  if (item.base) {
    const base = document.createElement('span');
    base.className = 'base-text';
    base.textContent = item.base;
    nameWrap.appendChild(base);
  }
  const flags = document.createElement('span');
  flags.className = 'tbl-flags';
  if (item.rarity === 'RELIC') flags.append('✨');
  if (item.corrupted) flags.append('💀');
  nameWrap.appendChild(flags);

  // Hover tooltip — renders the PoE-style card
  const tooltip = document.createElement('div');
  tooltip.className = 'tbl-tooltip';
  tooltip.appendChild(renderItemCard(slotLabel, item, result, rowIndex + 10000));
  nameWrap.appendChild(tooltip);

  row.appendChild(nameWrap);

  const linkEl = document.createElement('a');
  linkEl.className = 'trade-link';
  linkEl.href = result.url;
  linkEl.target = '_blank';
  linkEl.rel = 'noopener';
  linkEl.textContent = 'Trade ↗';
  row.appendChild(linkEl);

  const copyBtn = document.createElement('button');
  copyBtn.className = 'copy-btn';
  copyBtn.textContent = 'Copy';
  copyBtn.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(result.url);
      copyBtn.textContent = '✓';
      setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1200);
    } catch {
      copyBtn.textContent = '!';
    }
  });
  row.appendChild(copyBtn);

  const hasDetails = result.filters.length > 0 || result.unmatched.length > 0;
  if (hasDetails) {
    const toggle = document.createElement('button');
    toggle.className = 'details-toggle';
    toggle.textContent = 'Filters ▾';
    row.appendChild(toggle);

    const details = document.createElement('div');
    details.className = 'details';
    details.id = `tbl-details-${rowIndex}`;
    details.appendChild(renderEditableFilters(item, result, linkEl));

    toggle.addEventListener('click', () => {
      details.classList.toggle('open');
      toggle.textContent = details.classList.contains('open') ? 'Filters ▴' : 'Filters ▾';
    });

    row.appendChild(details);
  } else {
    row.appendChild(document.createElement('span'));
  }

  return row;
}

function renderItemCard(
  slotLabel: string,
  item: ParsedItem,
  result: BuildResult,
  rowIndex: number,
): HTMLElement {
  const card = document.createElement('div');
  card.className = 'poe-item rarity-' + rarityClass(item.rarity);

  // Slot tag
  const slotTag = document.createElement('div');
  slotTag.className = 'poe-slot-tag';
  slotTag.textContent = slotLabel;
  card.appendChild(slotTag);

  // Header: name + base
  const header = document.createElement('div');
  header.className = 'poe-header';
  const nameEl = document.createElement('div');
  nameEl.className = 'poe-name ' + rarityClass(item.rarity);
  nameEl.textContent = item.name || '(Unknown)';
  header.appendChild(nameEl);
  if (item.base) {
    const baseEl = document.createElement('div');
    baseEl.className = 'poe-base';
    baseEl.textContent = item.base;
    header.appendChild(baseEl);
  }
  card.appendChild(header);

  // Body wrapper (stats + mods)
  const body = document.createElement('div');
  body.className = 'poe-body';
  card.appendChild(body);

  // Defense stats
  const d = item.defense;
  const hasDefense = d && (d.ar || d.ev || d.es || d.ward);
  if (hasDefense) {
    if (d.ar != null) body.appendChild(makeRow(`Armour: <strong>${d.ar}</strong>`));
    if (d.ev != null) body.appendChild(makeRow(`Evasion Rating: <strong>${d.ev}</strong>`));
    if (d.es != null) body.appendChild(makeRow(`Energy Shield: <strong>${d.es}</strong>`));
    if (d.ward != null) body.appendChild(makeRow(`Ward: <strong>${d.ward}</strong>`));
    body.appendChild(makeSep());
  }

  // Implicits
  if (item.implicits.length) {
    for (const im of item.implicits) {
      const mod = document.createElement('div');
      mod.className = 'poe-mod implicit ' + modClass(im);
      mod.textContent = cleanMod(im);
      body.appendChild(mod);
    }
    body.appendChild(makeSep());
  }

  // Explicits
  if (item.explicits.length) {
    for (const ex of item.explicits) {
      const text = cleanMod(ex);
      if (!text || text.startsWith('Quality') || text.startsWith('Sockets')) continue;
      const mod = document.createElement('div');
      mod.className = 'poe-mod ' + modClass(ex);
      mod.textContent = text;
      body.appendChild(mod);
    }
  }

  // Flags (Corrupted, Relic)
  if (item.rarity === 'RELIC') {
    const relic = document.createElement('div');
    relic.className = 'poe-relic-tag';
    relic.textContent = '✨ Foil Unique';
    body.appendChild(relic);
  }
  if (item.corrupted) {
    const corr = document.createElement('div');
    corr.className = 'poe-corrupted';
    corr.textContent = 'Corrupted';
    body.appendChild(corr);
  }

  // Actions: Trade link + Copy + Filter-toggle
  const actions = document.createElement('div');
  actions.className = 'poe-actions';

  const linkEl = document.createElement('a');
  linkEl.className = 'trade-link';
  linkEl.href = result.url;
  linkEl.target = '_blank';
  linkEl.rel = 'noopener';
  linkEl.textContent = 'Search Trade ↗';
  actions.appendChild(linkEl);

  const copyBtn = document.createElement('button');
  copyBtn.className = 'copy-btn';
  copyBtn.textContent = 'Copy';
  copyBtn.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(result.url);
      copyBtn.textContent = '✓';
      setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1200);
    } catch {
      copyBtn.textContent = '!';
    }
  });
  actions.appendChild(copyBtn);

  const hasDetails = result.filters.length > 0 || result.unmatched.length > 0;
  let detailsEl: HTMLDivElement | null = null;
  if (hasDetails) {
    const toggle = document.createElement('button');
    toggle.className = 'details-toggle';
    toggle.textContent = 'Filters ▾';
    actions.appendChild(toggle);

    detailsEl = document.createElement('div');
    detailsEl.className = 'details';
    detailsEl.id = `details-${rowIndex}`;
    detailsEl.appendChild(renderEditableFilters(item, result, linkEl));

    toggle.addEventListener('click', () => {
      detailsEl!.classList.toggle('open');
      toggle.textContent = detailsEl!.classList.contains('open') ? 'Filters ▴' : 'Filters ▾';
    });
  }

  card.appendChild(actions);
  if (detailsEl) card.appendChild(detailsEl);

  return card;
}

function makeRow(html: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'poe-row';
  el.innerHTML = html;
  return el;
}

function makeSep(): HTMLHRElement {
  const sep = document.createElement('hr');
  sep.className = 'poe-sep';
  return sep;
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function renderEditableFilters(item: ParsedItem, result: BuildResult, linkEl: HTMLAnchorElement): HTMLElement {
  const container = document.createElement('div');
  container.className = 'editable-filters';

  // Read actual defense filter values from queryBase (source of truth)
  const qb = result.queryBase as { query?: { filters?: { armour_filters?: { filters?: Record<string, { min?: number }> } } } };
  const armourFilters = qb.query?.filters?.armour_filters?.filters;
  if (armourFilters) {
    const defParts: string[] = [];
    if (armourFilters.ar?.min != null) defParts.push(`Armour ≥ ${armourFilters.ar.min}`);
    if (armourFilters.es?.min != null) defParts.push(`ES ≥ ${armourFilters.es.min}`);
    if (armourFilters.ev?.min != null) defParts.push(`Evasion ≥ ${armourFilters.ev.min}`);
    if (defParts.length) {
      const p = document.createElement('div');
      p.className = 'filter-defense';
      p.innerHTML = `<strong>Defense filter (active):</strong> ${defParts.join(', ')}`;
      container.appendChild(p);
    }
  }

  if (!result.filters.length && !result.unmatched.length) {
    const empty = document.createElement('div');
    empty.className = 'filter-empty';
    empty.textContent = 'No auto-filters applied.';
    container.appendChild(empty);
    return container;
  }

  const updateUrl = () => {
    const url = rebuildUrl(result.league, result.queryBase, result.filters);
    linkEl.href = url;
    result.url = url;
  };

  const list = document.createElement('div');
  list.className = 'filter-list';
  for (const f of result.filters) {
    list.appendChild(renderFilterRow(f, updateUrl));
  }
  container.appendChild(list);

  if (result.unmatched.length) {
    const u = document.createElement('div');
    u.className = 'filter-unmatched';
    u.innerHTML = `<strong>Not auto-matched (set manually in trade UI):</strong>`;
    for (const m of result.unmatched) {
      const line = document.createElement('div');
      line.className = 'filter-item';
      line.textContent = '• ' + m;
      u.appendChild(line);
    }
    container.appendChild(u);
  }

  return container;
}

function renderFilterRow(f: EditableFilter, onChange: () => void): HTMLElement {
  const row = document.createElement('label');
  row.className = 'filter-row';
  if (f.kind === 'pseudo') row.classList.add('is-pseudo');
  if (f.isOption) row.classList.add('is-option');

  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.checked = !f.disabled;
  cb.addEventListener('change', () => {
    f.disabled = !cb.checked;
    row.classList.toggle('disabled', f.disabled);
    onChange();
  });
  row.appendChild(cb);

  const label = document.createElement('span');
  label.className = 'filter-label';
  label.textContent = f.label;
  row.appendChild(label);

  // Inputs depending on filter type
  if (f.isOption) {
    const opt = document.createElement('span');
    opt.className = 'filter-option';
    opt.textContent = f.optionText ? `= ${f.optionText}` : `= option ${f.optionValue}`;
    row.appendChild(opt);
  } else {
    // Numeric inputs — both min and max for all stats. User can leave either empty.
    const inputs = document.createElement('span');
    inputs.className = 'filter-inputs';

    inputs.appendChild(document.createTextNode('min '));
    const minInput = document.createElement('input');
    minInput.type = 'number';
    minInput.className = 'num-input';
    minInput.value = f.min != null ? String(f.min) : '';
    minInput.placeholder = '—';
    minInput.addEventListener('input', () => {
      const v = minInput.value.trim();
      f.min = v === '' ? null : Number(v);
      onChange();
    });
    inputs.appendChild(minInput);

    inputs.appendChild(document.createTextNode(' max '));
    const maxInput = document.createElement('input');
    maxInput.type = 'number';
    maxInput.className = 'num-input';
    maxInput.value = f.max != null ? String(f.max) : '';
    maxInput.placeholder = '—';
    maxInput.addEventListener('input', () => {
      const v = maxInput.value.trim();
      f.max = v === '' ? null : Number(v);
      onChange();
    });
    inputs.appendChild(maxInput);

    if (f.originalVal != null) {
      const pobMark = document.createElement('span');
      pobMark.className = 'pob-val';
      pobMark.textContent = `(PoB: ${f.originalVal})`;
      inputs.appendChild(pobMark);
    }
    row.appendChild(inputs);
  }

  row.classList.toggle('disabled', f.disabled);
  return row;
}

function renderDetails(item: ParsedItem, result: BuildResult): string {
  const parts: string[] = [];

  if (item.defense && Object.keys(item.defense).length) {
    const d = item.defense;
    const defParts: string[] = [];
    if (d.ar) defParts.push(`Armour ≥ ${Math.floor(d.ar * 0.9)}`);
    if (d.es) defParts.push(`ES ≥ ${Math.floor(d.es * 0.9)}`);
    if (d.ev) defParts.push(`Evasion ≥ ${Math.floor(d.ev * 0.9)}`);
    if (defParts.length) {
      parts.push(`<div class="filter-group"><strong>Defense:</strong> ${defParts.join(', ')}</div>`);
    }
  }

  if (result.pseudos.length) {
    const items = result.pseudos.map(p =>
      `<div class="filter-item">🔹 <strong>${PSEUDO_LABEL[p.pk] ?? p.pk}</strong> ≥ <span class="fval">${p.minVal}</span> <em>(PoB: ${p.total.toFixed(0)})</em></div>`
    ).join('');
    parts.push(`<div class="filter-group"><strong>Pseudo totals:</strong>${items}</div>`);
  }

  if (result.explicits.length) {
    const items = result.explicits.map(e => {
      const valLabel = e.filterVal != null
        ? (e.inverse ? `(≤ ${e.filterVal})` : `(≥ ${e.filterVal})`)
        : '';
      return `<div class="filter-item"><code>${esc(e.cat)}</code> — ${esc(e.text.replace(/\n/g, ' / '))} <span class="fval">${valLabel}</span></div>`;
    }).join('');
    parts.push(`<div class="filter-group"><strong>Mod filters:</strong>${items}</div>`);
  }

  if (result.unmatched.length) {
    const items = result.unmatched.map(u => `<div class="filter-item">• ${esc(u)}</div>`).join('');
    parts.push(`<div class="filter-group"><strong>Not auto-matched (set manually in UI):</strong>${items}</div>`);
  }

  return parts.join('');
}

function renderFromCode(code: string, itemSetId?: string): void {
  const pob = parsePoB(code, itemSetId);
  const league = leagueSelect.value;

  // Populate ItemSet picker (once, after parse)
  if (!itemSetId) {
    itemsetSelect.innerHTML = '';
    const activeId = pob.itemSets.find(s => s.active)?.id ?? '';
    for (const set of pob.itemSets) {
      const opt = document.createElement('option');
      opt.value = set.id;
      opt.textContent = (set.active ? '★ ' : '') + (set.title || `Set #${set.id}`);
      if (set.id === activeId) opt.selected = true;
      itemsetSelect.appendChild(opt);
    }
    itemsetSelect.disabled = pob.itemSets.length < 2;
  }

  const minRatio = parseFloat(minPctSelect.value) || 0.85;
  const { equipment, jewels, gemGroups } = buildLinks(league, pob.equipped, pob.jewels, pob.gemGroups, minRatio);

  const totalGems = gemGroups.reduce((n, g) => n + g.gems.length, 0);
  const setLabel = pob.itemSets.find(s => s.id === (itemSetId ?? pob.itemSets.find(x => x.active)?.id))?.title ?? '-';
  buildInfoEl.innerHTML = `
    <strong>${esc(pob.className)}</strong> / ${esc(pob.ascendClassName)} — Level ${esc(pob.level)}
    · League: <strong>${esc(league)}</strong>
    · Set: <strong>${esc(setLabel)}</strong>
    · ${equipment.length} items, ${jewels.length} jewels, ${totalGems} gems
  `;

  const mode = viewModeSelect.value; // 'table' or 'cards'
  const section = sectionSelect.value; // 'all' | 'equipment' | 'jewels' | 'gems'
  const render = mode === 'cards' ? renderItemCard : renderItemTable;
  equipmentEl.className = mode === 'cards' ? 'view-cards' : 'view-table';
  jewelsEl.className = mode === 'cards' ? 'view-cards' : 'view-table';

  const showEq = section === 'all' || section === 'equipment';
  const showJew = section === 'all' || section === 'jewels';
  const showGems = section === 'all' || section === 'gems';
  const eqHeading = document.getElementById('equipment-heading');
  if (eqHeading) eqHeading.classList.toggle('hidden', !showEq);
  equipmentEl.classList.toggle('hidden', !showEq);
  jewelsEl.classList.toggle('hidden', !showJew);
  gemsEl.classList.toggle('hidden', !showGems);

  equipmentEl.innerHTML = '';
  equipment.forEach((e, i) => {
    equipmentEl.appendChild(render(e.slot, e.item, e.result, i));
  });

  jewelsEl.innerHTML = '';
  if (jewels.length && showJew) {
    jewelsHeading.classList.remove('hidden');
    jewels.forEach((j, i) => {
      jewelsEl.appendChild(render(`Jewel ${i + 1}`, j.item, j.result, 100 + i));
    });
  } else {
    jewelsHeading.classList.add('hidden');
  }

  // Gem groups — one section per socket location (Helmet, Body Armour, Weapon 1, etc.)
  gemsEl.innerHTML = '';
  if (gemGroups.length && showGems) {
    gemsHeading.classList.remove('hidden');
    for (const gg of gemGroups) {
      const groupEl = document.createElement('div');
      groupEl.className = 'gem-group';

      const header = document.createElement('div');
      header.className = 'gem-group-header';
      const slotLabel = gg.group.label || gg.group.slot;
      header.textContent = slotLabel;
      groupEl.appendChild(header);

      for (const g of gg.gems) {
        const row = document.createElement('div');
        row.className = 'gem-row';
        const name = document.createElement('span');
        name.className = 'gem-name';
        if (g.gem.isAwakened) name.classList.add('awakened');
        else if (g.gem.isSupport) name.classList.add('support');
        name.textContent = g.gem.name;
        row.appendChild(name);
        const meta = document.createElement('span');
        meta.className = 'gem-meta';
        meta.textContent = `${g.gem.level}/${g.gem.quality}`;
        row.appendChild(meta);
        const link = document.createElement('a');
        link.className = 'trade-link';
        link.href = g.url;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = 'Trade ↗';
        row.appendChild(link);
        groupEl.appendChild(row);
      }
      gemsEl.appendChild(groupEl);
    }
  } else {
    gemsHeading.classList.add('hidden');
  }

  resultsSection.classList.remove('hidden');
  setStatus(`${equipment.length + jewels.length} links generated.`, 'success');
}

// Per-tab cooldown — prevents rapid spam clicks on Generate
let lastGenerateAt = 0;
const GENERATE_COOLDOWN_MS = 3000;

async function handleGenerate(): Promise<void> {
  const raw = input.value.trim();
  if (!raw) {
    setStatus('Please paste a PoB code or pobb.in URL.', 'error');
    return;
  }

  const now = Date.now();
  const elapsed = now - lastGenerateAt;
  if (elapsed < GENERATE_COOLDOWN_MS) {
    const wait = Math.ceil((GENERATE_COOLDOWN_MS - elapsed) / 1000);
    setStatus(`Slow down — try again in ${wait}s.`, 'error');
    return;
  }
  lastGenerateAt = now;

  generateBtn.disabled = true;
  // Cooldown countdown shown on button
  const originalText = generateBtn.textContent;
  let secondsLeft = Math.ceil(GENERATE_COOLDOWN_MS / 1000);
  const countdown = setInterval(() => {
    secondsLeft -= 1;
    if (secondsLeft <= 0) {
      clearInterval(countdown);
      generateBtn.textContent = originalText;
      generateBtn.disabled = false;
    } else {
      generateBtn.textContent = `Wait ${secondsLeft}s …`;
    }
  }, 1000);
  generateBtn.textContent = `Wait ${secondsLeft}s …`;

  setStatus('Loading stat index …');

  try {
    await loadStatIndex();
    setStatus('Decoding PoB …');
    const code = await fetchPoBFromUrl(raw);
    lastPobCode = code;
    renderFromCode(code);
    // Extract metadata for tracking — full build snapshot
    const pobbMatch = raw.match(/pobb\.in\/([A-Za-z0-9_-]+)/);
    const meta: Record<string, unknown> = {};
    try {
      const pob = parsePoB(code, itemsetSelect.value || undefined);
      if (pob.className) meta.cls = pob.className;
      if (pob.ascendClassName) meta.asc = pob.ascendClassName;
      if (pob.level) meta.lvl = parseInt(pob.level, 10) || 0;
      const s = pob.stats;
      const copy = ['life','lifePercent','es','ward','mana','ehp','fireRes','coldRes','lightningRes','chaosRes',
                    'evadeChance','blockChance','pdr','totalDPS','speed','hitChance','critChance','critMulti',
                    'bandit','pantheonMajor','pantheonMinor','configSummary'] as const;
      for (const k of copy) if (s[k] != null) meta[k] = s[k];
    } catch {}
    if (pobbMatch) {
      meta.pobbId = pobbMatch[1];
      trackPob(meta);
      setTimeout(refreshStats, 500);
    } else {
      // Raw code paste — auto-upload to pobb.in to get a shareable link.
      // Track now without ID; update with ID after upload completes.
      trackPob(meta);
      setTimeout(refreshStats, 500);
      uploadToPobbIn(code).then(id => {
        if (id) {
          meta.pobbId = id;
          // Use /track/recent so we don't double-count the PoB analysis
          trackPob(meta, true);
          setTimeout(refreshStats, 500);
        }
      });
    }
  } catch (err) {
    console.error(err);
    if (err instanceof UnsupportedPoBError) {
      setStatus(err.message, 'error');
      // Hide previous results so user doesn't see stale data
      resultsSection.classList.add('hidden');
    } else {
      setStatus(`Error: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  }
  // Don't override cooldown — interval handles re-enable
}

// Re-render when user picks different ItemSet or League
itemsetSelect.addEventListener('change', () => {
  if (lastPobCode) {
    try { renderFromCode(lastPobCode, itemsetSelect.value); }
    catch (err) { console.error(err); }
  }
});
function reRender() {
  saveSettings();
  if (lastPobCode) {
    try { renderFromCode(lastPobCode, itemsetSelect.value || undefined); }
    catch (err) { console.error(err); }
  }
}
leagueSelect.addEventListener('change', reRender);
viewModeSelect.addEventListener('change', reRender);
sectionSelect.addEventListener('change', reRender);
minPctSelect.addEventListener('change', reRender);

// Feedback form
const feedbackForm = document.getElementById('feedback-form') as HTMLFormElement | null;
const feedbackStatus = document.getElementById('feedback-status');
const loadedAtEl = document.getElementById('loaded-at') as HTMLInputElement | null;
if (loadedAtEl) loadedAtEl.value = String(Date.now());
feedbackForm?.addEventListener('submit', async (ev) => {
  ev.preventDefault();
  const fd = new FormData(feedbackForm);
  const message = String(fd.get('message') || '').trim();
  if (message.length < 10) {
    if (feedbackStatus) {
      feedbackStatus.textContent = 'Please write at least 10 characters.';
      feedbackStatus.className = 'status error';
    }
    return;
  }
  const body = {
    message,
    handle: String(fd.get('handle') || '').trim(),
    website: String(fd.get('website') || ''), // honeypot
    loaded_at: Number(fd.get('loaded_at') || 0),
  };
  if (feedbackStatus) {
    feedbackStatus.textContent = 'Sending…';
    feedbackStatus.className = 'status';
  }
  try {
    const resp = await fetch('/api/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (resp.ok) {
      feedbackForm.reset();
      if (loadedAtEl) loadedAtEl.value = String(Date.now());
      if (feedbackStatus) {
        feedbackStatus.textContent = 'Thanks — feedback saved ✓';
        feedbackStatus.className = 'status success';
      }
    } else {
      const txt = await resp.text();
      if (feedbackStatus) {
        feedbackStatus.textContent = `Couldn't save: ${txt || resp.status}`;
        feedbackStatus.className = 'status error';
      }
    }
  } catch (err) {
    if (feedbackStatus) {
      feedbackStatus.textContent = `Network error: ${err instanceof Error ? err.message : err}`;
      feedbackStatus.className = 'status error';
    }
  }
});

generateBtn.addEventListener('click', handleGenerate);
input.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleGenerate();
});

// Auto-load when arriving from a guide page: ?pob=<url-or-code>
const urlPobParam = new URLSearchParams(window.location.search).get('pob');
if (urlPobParam) {
  input.value = urlPobParam;
  void handleGenerate();
}
